/**
 * Business logic functions related to NCM
 */

import { ENTRADA_CONSTANTS, BRAZILIAN_STATES } from '../constants/tax-constants'
import { removePercentageMask } from './formatters'

export const getNCMValidationRules = (fieldName, isCreatingNCM = false) => {
  if (!isCreatingNCM) return {}
  
  if (fieldName === 'ncm_code') {
    return {
      required: 'Código NCM é obrigatório',
      pattern: {
        value: /^\d{4}\.\d{2}\.\d{2}$/,
        message: 'Formato: 0000.00.00'
      }
    }
  }
  
  if (fieldName === 'ncm_description') {
    return { required: 'Descrição é obrigatória' }
  }

  if (fieldName.includes('_aliquota')) {
    return {
      defaultValue: 0,
      required: 'Campo obrigatório',
      min: { value: 0, message: 'Alíquota deve ser maior ou igual a 0%' },
      max: { value: 100, message: 'Alíquota deve ser no máximo 100%' },
      validate: value => {
        if (value && !isNaN(value)) {
          const numValue = parseFloat(value)
          if (numValue < 0) return 'Valor deve ser positivo'
          if (numValue > 100) return 'Alíquota deve ser no máximo 100%'
        }
        return true
      }
    }
  }

  if (fieldName.includes('fcp_')) {
    return {
      defaultValue: '',
      validate: value => {
        if (!value || value.trim() === '') return true // Campo opcional
        
        const cleanValue = removePercentageMask(value)
        const numValue = parseFloat(cleanValue)
        
        if (isNaN(numValue)) return 'Formato inválido'
        if (numValue < 0) return 'Valor deve ser positivo'
        if (numValue > 100) return 'FCP deve ser no máximo 100%'
        
        return true
      }
    }
  }

  if (fieldName.includes('icms_interno_')) {
    return {
      defaultValue: '',
      validate: value => {
        if (!value || value.trim() === '') return true // Campo opcional
        
        const cleanValue = removePercentageMask(value)
        const numValue = parseFloat(cleanValue)
        
        if (isNaN(numValue)) return 'Formato inválido'
        if (numValue < 0) return 'Valor deve ser positivo'
        if (numValue > 100) return 'ICMS Interno deve ser no máximo 100%'
        
        return true
      }
    }
  }

  if (fieldName.includes('_reduction')) {
    return {
      required: 'Campo obrigatório',
      min: { value: 0, message: 'Redução deve ser maior ou igual a 0%' },
      max: { value: 100, message: 'Redução deve ser no máximo 100%' },
      validate: value => {
        if (value && !isNaN(value)) {
          const numValue = parseFloat(value)
          if (numValue < 0) return 'Valor deve ser positivo'
          if (numValue > 100) return 'Redução não pode ser maior que 100%'
        }
        return true
      }
    }
  }

  if (fieldName.includes('_st')) {
    return { required: 'Código de situação tributária é obrigatório' }
  }

  if (fieldName.includes('_cod_enq')) {
    return {
      required: 'Código de enquadramento é obrigatório',
      pattern: {
        value: /^\d{3}$/,
        message: 'Código deve ter 3 dígitos (ex: 999)'
      }
    }
  }

  return {}
}

export const processNCMFormData = (formData) => {
  const processedData = { ...formData }
  
  BRAZILIAN_STATES.filter(state => state.value !== '').forEach(state => {
    const fieldName = `fcp_${state.value.toLowerCase()}`
    if (processedData[fieldName]) {
      const cleanValue = removePercentageMask(processedData[fieldName])
      processedData[fieldName] = parseFloat(cleanValue) || 0
    }
  })
  
  return {
    ...processedData, 
    envio_cofins_st_entrada: ENTRADA_CONSTANTS.COFINS_ST_ENTRADA,
    envio_pis_st_entrada: ENTRADA_CONSTANTS.PIS_ST_ENTRADA,
    envio_ipi_st_entrada: ENTRADA_CONSTANTS.IPI_ST_ENTRADA
  }
}

export const prepareNCMDataForAPI = (formData) => {
  const ncmData = {
    code: formData.ncm_code,
    description: formData.ncm_description,
    exception_code: formData.exception_code,
    "Contribuinte": {
      "IPI_ST": formData.contribuinte_ipi_st,
      "IPI_ALIQUOTA": parseFloat(formData.contribuinte_ipi_aliquota || 0),
      "IPI_COD_ENQ": formData.contribuinte_ipi_cod_enq,
      "PIS_ST": formData.contribuinte_pis_st,
      "PIS_ALIQUOTA": parseFloat(formData.contribuinte_pis_aliquota || 0),
      "COFINS_ST": formData.contribuinte_cofins_st,
      "COFINS_ALIQUOTA": parseFloat(formData.contribuinte_cofins_aliquota || 0),
      "ICMS_CST_OUTSITE_STATE": formData.contribuinte_icms_cst_outsite,
      "ICMS_CST_INSIDE_STATE": formData.contribuinte_icms_cst_inside,
      "ICMS_REDUCTION_CALC_BASE_INSIDE_STATE": parseFloat(formData.contribuinte_icms_reduction || 0)
    },
    "Não contribuinte": {
      "IPI_ST": formData.nao_contribuinte_ipi_st,
      "IPI_ALIQUOTA": parseFloat(formData.nao_contribuinte_ipi_aliquota || 0),
      "IPI_COD_ENQ": formData.nao_contribuinte_ipi_cod_enq,
      "PIS_ST": formData.nao_contribuinte_pis_st,
      "PIS_ALIQUOTA": parseFloat(formData.nao_contribuinte_pis_aliquota || 0),
      "COFINS_ST": formData.nao_contribuinte_cofins_st,
      "COFINS_ALIQUOTA": parseFloat(formData.nao_contribuinte_cofins_aliquota || 0),
      "ICMS_CST_OUTSITE_STATE": formData.nao_contribuinte_icms_cst_outsite,
      "ICMS_CST_INSIDE_STATE": formData.nao_contribuinte_icms_cst_inside,
      "ICMS_REDUCTION_CALC_BASE_INSIDE_STATE": parseFloat(formData.nao_contribuinte_icms_reduction || 0)
    },
    "Envio de estoque (Transferência ou Remessa)": {
      "IPI_ST": formData.envio_ipi_st,
      "IPI_ALIQUOTA": parseFloat(formData.envio_ipi_aliquota || 0),
      "IPI_COD_ENQ": formData.envio_ipi_cod_enq,
      "PIS_ST": formData.envio_pis_st,
      "PIS_ALIQUOTA": parseFloat(formData.envio_pis_aliquota || 0),
      "COFINS_ST": formData.envio_cofins_st,
      "COFINS_ALIQUOTA": parseFloat(formData.envio_cofins_aliquota || 0),
      "ICMS_CST_OUTSITE_STATE": formData.envio_icms_cst_outsite,
      "ICMS_CST_INSIDE_STATE": formData.envio_icms_cst_inside,
      "ICMS_REDUCTION_CALC_BASE_INSIDE_STATE": parseFloat(formData.envio_icms_reduction || 0),
      "COFINS_ST_ENTRADA": formData.envio_cofins_st_entrada,
      "PIS_ST_ENTRADA": formData.envio_pis_st_entrada,
      "IPI_ST_ENTRADA": formData.envio_ipi_st_entrada
    },
    "Aliquota_FCP": generateAliquotaData(formData, 'fcp'),
    "Aliquota_ICMS_Interno": generateAliquotaData(formData, 'icms_interno')
  }

  return ncmData
}

const generateAliquotaData = (formData, prefix) => {
  const aliquotaData = {}
  const states = ['ac', 'al', 'am', 'ap', 'ba', 'ce', 'df', 'es', 'go', 'ma', 'mt', 'ms', 'mg', 'pa', 'pb', 'pr', 'pe', 'pi', 'rj', 'rn', 'rs', 'ro', 'rr', 'sc', 'sp', 'se', 'to']
  
  states.forEach(state => {
    const fieldName = `${prefix}_${state}`
    if (formData[fieldName]) {
      const cleanValue = formData[fieldName].toString().replace(/,/g, '.')
      const numValue = parseFloat(cleanValue)
      if (!isNaN(numValue) && numValue > 0) {
        aliquotaData[state.toUpperCase()] = numValue
      }
    }
  })
  
  return aliquotaData
}

export const getDefaultFCPValues = () => {
  const defaults = {}
  BRAZILIAN_STATES.filter(state => state.value !== '').forEach(state => {
    defaults[`fcp_${state.value.toLowerCase()}`] = ''
  })
  return defaults
}

export const getDefaultNCMFormValues = () => {
  return {
    ncm_code: '',
    ncm_description: '',
    // Contribuinte
    contribuinte_ipi_st: '',
    contribuinte_ipi_aliquota: 0,
    contribuinte_ipi_cod_enq: '999',
    contribuinte_pis_st: '',
    contribuinte_pis_aliquota: 0,
    contribuinte_cofins_st: '',
    contribuinte_cofins_aliquota: 0,
    contribuinte_icms_cst_outsite: '',
    contribuinte_icms_cst_inside: '',
    contribuinte_icms_reduction: 0,
    // Não contribuinte
    nao_contribuinte_ipi_st: '',
    nao_contribuinte_ipi_aliquota: 0,
    nao_contribuinte_ipi_cod_enq: '999',
    nao_contribuinte_pis_st: '',
    nao_contribuinte_pis_aliquota: 0,
    nao_contribuinte_cofins_st: '',
    nao_contribuinte_cofins_aliquota: 0,
    nao_contribuinte_icms_cst_outsite: '',
    nao_contribuinte_icms_cst_inside: '',
    nao_contribuinte_icms_reduction: 0,
    // Envio de estoque
    envio_ipi_st: '',
    envio_ipi_aliquota: 0,
    envio_ipi_cod_enq: '999',
    envio_pis_st: '',
    envio_pis_aliquota: 0,
    envio_cofins_st: '',
    envio_cofins_aliquota: 0,
    envio_icms_cst_outsite: '',
    envio_icms_cst_inside: '',
    envio_icms_reduction: 0,
    envio_cofins_st_entrada: '',
    envio_pis_st_entrada: '',
    envio_ipi_st_entrada: '',
    // FCP
    ...getDefaultFCPValues()
  }
} 